import { difference, flatMap, forEach, keyBy, reduce, uniq, unset } from 'lodash';
import { combineReducers } from 'redux';
import { AppsDeletedAction, APPS_DELETED, DeletedAppEntityPayload } from '../actions/app';
import {
    AppGroupsUpdatedAction,
    AppGroupStateUpdatedAction,
    ConnectedAppGroupEntityResponse,
    APP_GROUP_STATE_UPDATED,
    APP_GROUPS_UPDATED,
    AppGroupsDeletedAction,
    DeletedAppGroupPayload,
    APP_GROUPS_DELETED,
} from '../actions/appGroups';
import { LogoutSuccessAction, LOGOUT_SUCCESS } from '../actions/login';
import { OrgStateUpdatedAction } from '../actions/organization';
import { ClearStateAction, CLEAR_STATE, FetchStateSuccessAction, FETCH_STATE_SUCCESS } from '../actions/state';
import { AppGroup } from '../types';

interface AppGroupsById {
    [id: string]: AppGroup;
}

type AppGroupsActionTypes =
    | FetchStateSuccessAction
    | OrgStateUpdatedAction
    | LogoutSuccessAction
    | AppGroupsUpdatedAction
    | AppGroupStateUpdatedAction
    | AppGroupsDeletedAction
    | AppsDeletedAction
    | ClearStateAction;

function createAppGroupsById(state, action: FetchStateSuccessAction): AppGroupsById {
    const allAppGroups = flatMap(action.payload.organizations.map((org) => org.appGroups));
    const appGroupsById = keyBy(allAppGroups, (appGroup) => appGroup.id);

    return {
        ...state,
        ...appGroupsById,
    };
}

function updateAppGroup(state, payload: ConnectedAppGroupEntityResponse): AppGroupsById {
    return {
        ...state,
        [payload.appGroup.id]: {
            ...payload.appGroup,
        },
    };
}

function updateAppGroups(state, payload: ConnectedAppGroupEntityResponse[]): AppGroupsById {
    return reduce(
        payload,
        (updatedState, connectedAppGroup) => {
            return updateAppGroup(updatedState, connectedAppGroup);
        },
        state,
    );
}

function deleteAppGroups(state, appGroups: DeletedAppGroupPayload[]) {
    const updatedState = {
        ...state,
    };

    forEach(appGroups, (deleteAppGroupEntity) => {
        unset(updatedState, deleteAppGroupEntity.appGroupId);
    });

    return updatedState;
}

function deleteAppInAppGroup(state: AppGroupsById, deletedApp: DeletedAppEntityPayload) {
    const prevAppGroup = state[deletedApp.appGroupId];
    const prevAppIds = prevAppGroup?.appIds ?? [];

    return {
        ...state,
        [deletedApp.appGroupId]: {
            ...prevAppGroup,
            id: deletedApp.appGroupId,
            appIds: prevAppIds.filter((appId) => appId !== deletedApp.appId),
        },
    };
}

function deleteAppsInAppGroups(state: AppGroupsById, payload: DeletedAppEntityPayload[]) {
    return reduce(
        payload,
        (updatedState, deletedAppEntity) => {
            return deleteAppInAppGroup(updatedState, deletedAppEntity);
        },
        state,
    );
}

function appGroupsById(state: AppGroupsById = {}, action: AppGroupsActionTypes): AppGroupsById {
    switch (action.type) {
        case FETCH_STATE_SUCCESS:
            return createAppGroupsById(state, action);
        case APP_GROUP_STATE_UPDATED:
            return updateAppGroup(state, action.payload);
        case APP_GROUPS_UPDATED:
            return updateAppGroups(state, action.payload);
        case APP_GROUPS_DELETED:
            return deleteAppGroups(state, action.payload);
        case APPS_DELETED:
            return deleteAppsInAppGroups(state, action.payload);
        case CLEAR_STATE:
        case LOGOUT_SUCCESS:
            return {};
        default:
            return state;
    }
}

function allIds(state: string[] = [], action: AppGroupsActionTypes): string[] {
    switch (action.type) {
        case FETCH_STATE_SUCCESS: {
            const allAppGroups = action.payload.organizations.map((org) =>
                org.appGroups.map((appGroup) => appGroup.id),
            );

            return flatMap(allAppGroups);
        }
        case APP_GROUP_STATE_UPDATED: {
            state.push(action.payload.appGroup.id);

            return uniq(state);
        }
        case APP_GROUPS_UPDATED: {
            const newAppGroupIds = action.payload.map((connectedAppGroup) => connectedAppGroup.appGroup.id);
            const updatedAppGroupIds = state.concat(newAppGroupIds);

            return uniq(updatedAppGroupIds);
        }
        case APP_GROUPS_DELETED: {
            const deletedAppGroupIds = action.payload.map((app) => app.appGroupId);
            return difference(state, deletedAppGroupIds);
        }
        case CLEAR_STATE:
        case LOGOUT_SUCCESS:
            return [];
        default:
            return state;
    }
}

/*
    appGroups {
        allIds: ["1", "2", "3"],
        byId: {
            "1": {
                ...appGroup
            },
            "2": {
                ...appGroup2
            },
            "3": {
                ...appGroup3
            }
        }
    }

*/

const appGroupsReducer = combineReducers({
    byId: appGroupsById,
    allIds: allIds,
});

export default appGroupsReducer;
